/**
 * Display-title sanitiser — defensive fallback for user-facing render sites
 * that may receive raw upload IDs or URI-form filenames.
 *
 * Backend `job_id` is `str(uuid.uuid4())[:8]` (8-char hex). Backend `file_id`
 * is `upload_<job_id>`. Both can leak through to UI render slots when a job's
 * `result.title` is undefined (failed or in-flight upload-batch jobs).
 *
 * Pass title candidates in order of preference. Returns the first candidate
 * that is a sane display string (non-empty, not a raw upload-id shape, not a
 * bare `upload://` scheme). Falls back to 'Untitled upload'.
 *
 * @example
 *   displayTitle(job.title, job.url)         // batch row with title fallback
 *   displayTitle(job.title)                  // single-candidate render site
 *   displayTitle(notification.title)         // notification popup
 */

const RAW_UPLOAD_ID_RE = /^upload_[0-9a-f]{8,}$/i;
const RAW_8CHAR_HEX_RE = /^[0-9a-f]{8}$/i;
const RAW_RSS_ID_RE = /^rss_[0-9a-f]{8,}$/i;
const UPLOAD_URI_PREFIX = 'upload://';
const FALLBACK = 'Untitled upload';

function isRawUploadId(s: string): boolean {
  return (
    RAW_UPLOAD_ID_RE.test(s) ||
    RAW_8CHAR_HEX_RE.test(s) ||
    RAW_RSS_ID_RE.test(s)
  );
}

export function displayTitle(...candidates: Array<string | null | undefined>): string {
  for (const c of candidates) {
    if (!c || typeof c !== 'string') continue;
    const t = c.trim();
    if (!t) continue;
    if (isRawUploadId(t)) continue;
    if (t.startsWith(UPLOAD_URI_PREFIX)) {
      const fname = t.slice(UPLOAD_URI_PREFIX.length).trim();
      if (fname && !isRawUploadId(fname)) return fname;
      continue;
    }
    return t;
  }
  return FALLBACK;
}
